import { computed, Injectable, isDevMode, signal } from '@angular/core';

const STORAGE_KEY = 'config.debug';

/**
 * Customer numbers starting with this prefix are internal/dev accounts (e.g. `99999935`).
 * Backend guarantee: this sequence is never assigned to real customers.
 */
export const INTERNAL_CUSTOMER_PREFIX = '999999';

export function isInternalCustomer(customer: string | null | undefined): boolean {
    return typeof customer === 'string' && customer.startsWith(INTERNAL_CUSTOMER_PREFIX);
}

/**
 * The account decides debug authorization — in any build type. `devModeFallback` only
 * applies while the `customer` claim is not accessible (pre-login, logged out, app
 * without auth wiring): dev builds stay authorized, production fails closed.
 */
export function computeDebugAuthorization(
    customer: string | null | undefined,
    devModeFallback: boolean
): boolean {
    if (typeof customer === 'string' && customer.length > 0) {
        return isInternalCustomer(customer);
    }
    return devModeFallback;
}

function readFromStorage(): boolean {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'true' || raw === '1';
}

const debugEnabled = signal(readFromStorage());
// Initial value = the fallback: the customer claim is unknown at module load, and
// isDevMode() is the one signal that is always checkable. provideDebugConfig() keeps
// this in sync with the JWT `customer` claim once auth state is available.
const debugAuthorized = signal(isDevMode());
const effectiveEnabled = computed(() => debugEnabled() && debugAuthorized());

export function isDebugEnabled(): boolean {
    return effectiveEnabled();
}

export function setDebugEnabled(value: boolean): boolean {
    if (value) {
        localStorage.setItem(STORAGE_KEY, 'true');
    } else {
        // Disabled = absence of the key. Keeps DevTools storage clean and matches
        // the parse rule (any non-truthy value, including missing, is false).
        localStorage.removeItem(STORAGE_KEY);
    }
    debugEnabled.set(value);
    return value;
}

/**
 * Re-evaluates debug authorization for the given `customer` claim. Wired to
 * `AuthServiceFacade.customer$` by `provideDebugConfig()`; the `devModeFallback`
 * parameter exists so tests can exercise the production-fallback path.
 */
export function updateDebugAuthorization(
    customer: string | null | undefined,
    devModeFallback = isDevMode()
): void {
    debugAuthorized.set(computeDebugAuthorization(customer, devModeFallback));
}

@Injectable({ providedIn: 'root' })
export class DebugConfigService {
    readonly enabled = effectiveEnabled;
    /** Whether this user may toggle debug mode (gates e.g. the Ctrl+Shift+D shortcut). */
    readonly canToggle = debugAuthorized.asReadonly();

    toggle(): boolean {
        return setDebugEnabled(!debugEnabled());
    }

    setEnabled(value: boolean): void {
        setDebugEnabled(value);
    }
}
